/**
 * Dev-mode API response shape validators.
 * These log warnings when the API response shape doesn't match expectations.
 * In production, validation is skipped entirely.
 */

const IS_DEV = import.meta.env.DEV;

function warnShape(path: string, field: string, expected: string, got: unknown): void {
  console.warn(
    `[API] ${path} response shape mismatch: "${field}" expected ${expected}, got ${typeof got}`,
    got
  );
}

/** Validate that a value is a non-null object */
function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/**
 * Validate a Tenant response shape
 */
export function validateTenantShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  if (!('id' in data)) {
    warnShape(path, 'id', 'present', undefined);
    return false;
  }
  // Tenant must have at least companyName or contactName
  if (!('companyName' in data) && !('contactName' in data)) {
    warnShape(path, 'companyName|contactName', 'at least one present', undefined);
    return false;
  }
  return true;
}

/**
 * Validate a Payment response shape
 */
export function validatePaymentShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id', 'leaseId', 'amount', 'paymentDate'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  if (typeof data.amount !== 'number' && typeof data.amount !== 'string') {
    warnShape(path, 'amount', 'number|string', data.amount);
  }
  return true;
}

/**
 * Validate a Charge response shape
 */
export function validateChargeShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id', 'leaseId', 'amount', 'type', 'dueDate'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a PaymentSummary response shape
 */
export function validatePaymentSummaryShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['paymentCount', 'totalPaid', 'totalPrincipalPaid', 'totalInterestPaid', 'totalLateFeesPaid'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a ChargeSummary response shape
 */
export function validateChargeSummaryShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['totalCharged', 'totalBalance'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a Lease response shape
 */
export function validateLeaseShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id', 'status', 'startDate'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a Ledger entry response shape
 */
export function validateLedgerEntryShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id', 'date', 'type', 'runningBalance'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a Property response shape
 */
export function validatePropertyShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a Unit response shape
 */
export function validateUnitShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a RentSchedule response shape
 */
export function validateRentScheduleShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id', 'startDate', 'baseRent'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a Guarantor response shape
 */
export function validateGuarantorShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id', 'name'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a TaxParcel response shape
 */
export function validateTaxParcelShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id', 'parcelId'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate an Inspection response shape
 */
export function validateInspectionShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id', 'unitId'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate an InsurancePolicy response shape
 */
export function validateInsurancePolicyShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a Dashboard response shape
 */
export function validateDashboardShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['tenants', 'summary'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate an ActionItem response shape
 */
export function validateActionItemShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id', 'type', 'severity', 'title'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a ComplianceReport response shape
 */
export function validateComplianceReportShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['inspections', 'insurance', 'occupancy'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
    if (!Array.isArray(data[field])) {
      warnShape(path, field, 'array', data[field]);
      return false;
    }
  }
  return true;
}

/**
 * Validate an AssessorSnapshot response shape
 */
export function validateAssessorSnapshotShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  const required = ['id', 'taxParcelId', 'fetchedAt'];
  for (const field of required) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate an ActionItems container response shape ({ items, summary })
 */
export function validateActionItemsResponseShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  if (!('items' in data) || !Array.isArray(data.items)) {
    warnShape(path, 'items', 'array', data.items);
    return false;
  }
  if (data.items.length > 0) {
    return validateActionItemShape(data.items[0], `${path}.items[0]`);
  }
  return true;
}

/**
 * Validate a PaymentPreview response shape (allocations, totalAllocated, remainingPayment)
 */
export function validatePaymentPreviewShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  if (!('allocations' in data) || !Array.isArray(data.allocations)) {
    warnShape(path, 'allocations', 'array', data.allocations);
    return false;
  }
  for (const field of ['totalAllocated', 'remainingPayment']) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate an Attachment response shape (upload result)
 */
export function validateAttachmentShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  for (const field of ['id', 'url', 'originalFilename']) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate a BalanceSummary response shape
 */
export function validateBalanceSummaryShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  for (const field of ['unpaidRent', 'unpaidUtilities', 'lateFees', 'interest', 'other', 'totalDue']) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
    if (typeof data[field] !== 'number') {
      warnShape(path, field, 'number', data[field]);
      return false;
    }
  }
  return true;
}

/**
 * Validate an ActionItems summary response shape ({ total, urgent })
 */
export function validateActionItemsSummaryShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  for (const field of ['total', 'urgent']) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate an AuditLog response shape
 */
export function validateAuditLogShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  for (const field of ['id', 'action', 'createdAt']) {
    if (!(field in data)) {
      warnShape(path, field, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate Global Search response shape ({ results: SearchResult[] }).
 */
export function validateSearchResultsShape(data: unknown, path: string): boolean {
  if (!IS_DEV) return true;
  if (!isObject(data)) {
    warnShape(path, 'data', 'object', data);
    return false;
  }
  if (!('results' in data) || !Array.isArray(data.results)) {
    warnShape(path, 'results', 'array', data.results);
    return false;
  }
  if (data.results.length === 0) return true;

  const first = data.results[0];
  if (!isObject(first)) {
    warnShape(path, 'results[0]', 'object', first);
    return false;
  }
  for (const field of ['id', 'type', 'title']) {
    if (!(field in first)) {
      warnShape(path, `results[0].${field}`, 'present', undefined);
      return false;
    }
  }
  return true;
}

/**
 * Validate an array response — checks first item's shape
 */
export function validateArrayResponse(
  data: unknown,
  path: string,
  itemValidator: (data: unknown, path: string) => boolean
): boolean {
  if (!IS_DEV) return true;
  if (!Array.isArray(data)) {
    warnShape(path, 'data', 'array', data);
    return false;
  }
  if (data.length > 0) {
    return itemValidator(data[0], `${path}[0]`);
  }
  return true;
}
